/**
 * Notification Hooks
 * Story 11.4: Notification System
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api/client';

// ============== Types ==============

export interface Notification {
  id: string;
  user_id: string;
  notification_type: string;
  title: string;
  message: string;
  resource_type?: string;
  resource_id?: string;
  metadata?: Record<string, any>;
  read: boolean;
  created_at: string;
  read_at?: string;
  expires_at: string;
}

export interface NotificationListResponse {
  items: Notification[];
  total: number;
  unread_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface NotificationPreferences {
  [notificationType: string]: {
    in_app: boolean;
    email: boolean;
    frequency: 'real_time' | 'daily_digest' | 'weekly_digest';
  };
}

// ============== Hooks ==============

/**
 * Fetch notifications
 */
export function useNotifications(unreadOnly: boolean = false, limit: number = 20) {
  // Check for SSR safety - only check existence for enabled flag
  const isAuthenticated = typeof window !== 'undefined' && !!localStorage.getItem('access_token');

  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', { unreadOnly, limit }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (unreadOnly) params.append('unread_only', 'true');
      params.append('limit', limit.toString());

      const response = await apiClient.get<NotificationListResponse>(
        `/notifications?${params}`
      );
      return response.data;
    },
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30000 : false,
  });
}

/**
 * Get unread count
 */
export function useUnreadCount() {
  // Check for SSR safety - only check existence for enabled flag
  const isAuthenticated = typeof window !== 'undefined' && !!localStorage.getItem('access_token');

  return useQuery<number>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await apiClient.get<NotificationListResponse>(
        `/notifications?unread_only=true&limit=1`
      );
      return response.data.unread_count;
    },
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30000 : false,
  });
}

/**
 * Mark notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (notificationId: string) => {
      await apiClient.post(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; marked_count: number }, Error>({
    mutationFn: async () => {
      const response = await apiClient.post<{ success: boolean; marked_count: number }>(
        `/notifications/read-all`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Delete notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (notificationId: string) => {
      await apiClient.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Get notification preferences
 */
export function useNotificationPreferences() {
  // Check for SSR safety - only check existence for enabled flag
  const isAuthenticated = typeof window !== 'undefined' && !!localStorage.getItem('access_token');

  return useQuery<{ preferences: NotificationPreferences }>({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const response = await apiClient.get<{ preferences: NotificationPreferences }>(
        `/notifications/preferences`
      );
      return response.data;
    },
    enabled: isAuthenticated,
  });
}

/**
 * Update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation<
    { preferences: NotificationPreferences },
    Error,
    { preferences: NotificationPreferences }
  >({
    mutationFn: async ({ preferences }) => {
      const response = await apiClient.patch<{ preferences: NotificationPreferences }>(
        `/notifications/preferences`,
        { preferences }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}

// ============== Utility Functions ==============

/**
 * Get icon name for notification type
 */
export function getNotificationIcon(type: string): string {
  if (type.startsWith('campaign_')) return 'Target';
  if (type.startsWith('budget_')) return 'DollarSign';
  if (type.startsWith('content_')) return 'FileText';
  if (type.startsWith('team_') || type === 'mention') return 'Users';
  if (type.startsWith('ai_') || type === 'performance_alert') return 'Sparkles';
  if (type.startsWith('integration_')) return 'Plug';
  if (type.startsWith('payment_') || type.startsWith('subscription_') || type.startsWith('trial_')) return 'CreditCard';
  return 'Bell';
}

/**
 * Get color class for notification type
 */
export function getNotificationColor(type: string): string {
  if (type.includes('launched') || type.includes('approved'))
    return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
  if (type.includes('exceeded') || type.includes('failed') || type.includes('error'))
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
  if (type.includes('warning') || type.includes('ending'))
    return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
  return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30';
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

/**
 * Get resource link
 */
export function getResourceLink(resourceType?: string, resourceId?: string): string | null {
  if (!resourceType || !resourceId) return null;

  const linkMap: Record<string, string> = {
    campaign: `/campaigns/${resourceId}`,
    content: `/content/${resourceId}`,
    workflow: `/workflows/${resourceId}`,
    activity: `/activity`,
    user: `/team/${resourceId}`,
  };

  return linkMap[resourceType] || null;
}
